import { Inject, Injectable } from "@angular/core";
import { GridDataResult } from "@progress/kendo-angular-grid";
import { HttpClient } from "@angular/common/http";
import { AppConstant } from "src/app/app.constant";
import { forkJoin, Observable, ReplaySubject } from "rxjs";
import { map } from "rxjs/operators";
import { DataResult } from "@progress/kendo-data-query";
import { DOCUMENT } from '@angular/common';
import { DataToDelete } from "src/app/_metronic/core/classes/core.class";
import { ToasterService, ToasterType } from "src/app/services/toaster/toaster.service";

@Injectable()
export class FeeService {
  apiUrl: string;
  kendoDataUrl: string;
  private _loadedLibraries: { [url: string]: ReplaySubject<any> } = {};
  constructor(
    @Inject(DOCUMENT) private readonly document: any,
    private httpClient: HttpClient,
    private appConstant: AppConstant, private toasterService: ToasterService,) {
    this.apiUrl = this.appConstant.getUrl()
  }
  // forkjoin parameters will grow when we are adding any new external library into app
  lazyLoadLibrary(resourceURL): Observable<any> {
    return forkJoin([
      this.loadScript(resourceURL)
    ]);
  }
  //Handled loading the external library start
  private loadScript(url: string): Observable<any> {
    if (this._loadedLibraries[url]) {
      return this._loadedLibraries[url].asObservable();
    }

    this._loadedLibraries[url] = new ReplaySubject();

    const script = this.document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = url;
    script.onload = () => {
      this._loadedLibraries[url].next();
      this._loadedLibraries[url].complete();
    };

    this.document.body.appendChild(script);
    return this._loadedLibraries[url].asObservable();
  }
  // Handled loading the external library end
  manageFee(fees) {
    return this.httpClient.post(`${this.apiUrl}/manageFees`, fees).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  public getAllFees(filters): Observable<DataResult> {
    return this.httpClient.post(`${this.apiUrl}/getAllFees/`, filters).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        } as GridDataResult
      })
    ) as any as Observable<GridDataResult>
  }

  public getAllFeesName(filters): Observable<DataResult> {
    return this.httpClient.post(`${this.apiUrl}/getAllFeesName/`, filters).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        } as GridDataResult
      })
    ) as any as Observable<GridDataResult>
  }

  public updateFeesPaymentNon(filters) {
    return this.httpClient.post(`${this.apiUrl}/updateFeesPayment`, filters).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  public downloadFeesPaymentInvoice(Id) {
    return new Promise((resolve, reject) => {
      this.httpClient.get(`${this.apiUrl}/downloadFeesPaymentInvoice/` + Id, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 403) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }

  public downloadStudentLedger(Id) {
    return new Promise((resolve, reject) => {
        this.httpClient
            .get(`${this.apiUrl}/downloadStudentFeesLedger/` + Id, { responseType: 'blob' })
            .pipe(map((res) => res))
            .subscribe(
                (res) => {
                    resolve(res);
                },
                (err) => {
                    if (err.status == 422) {
                        this.toasterService.showToaster(null, 'Student data not found!', ToasterType.Error);
                    }
                    reject(err);
                }
            );
    });
  };

  public downloadFeesReceipt(Id) {
    return new Promise((resolve, reject) => {
      this.httpClient.get(`${this.apiUrl}/downloadFeesReceipt/` + Id, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 403) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }

  async applyCouponCode(code): Promise<any> {
    return (await this.httpClient.post<GridDataResult>(`${this.apiUrl}/applyCouponCode/`, { CouponCode: code }).toPromise());
  }

  // get already paid fees by student
  async getPaidFeesByStudent(studentAcademicYearMappingId): Promise<any> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getPaidFeesByStudent/` + studentAcademicYearMappingId).toPromise()).data;
  }

  // save fees data to Student
  async addFeesToStudent(feesData): Promise<any> {
    return (await this.httpClient.post<GridDataResult>(`${this.apiUrl}/addFeesToStudent`, feesData).toPromise());
  }
  async bulkAddFeesToStudent(feesData): Promise<any> {
    return (await this.httpClient.post<GridDataResult>(`${this.apiUrl}/bulkAddFeesToStudent`, feesData).toPromise());
  }
  async updateFeesPayment(feesData): Promise<any> {
    return (await this.httpClient.post<GridDataResult>(`${this.apiUrl}/updateFeesPayment`, feesData).toPromise());
  }
  async getFeeById(Id): Promise<any> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getFeesById/` + Id).toPromise()).data;
  }
  async getFeesPaymentById(Id): Promise<any> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getFeesPaymentById/` + Id).toPromise()).data;
  }
  // async getFeesInvoiceData(Id): Promise<any> {
  //   return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getFeesInvoiceData/` + Id).toPromise()).data;
  // }
  async getFeesInvoiceData(Id): Promise<any> {
    // return this.httpClient.get(`${this.apiUrl}/getFeesPaymentInvoiceData/` + Id).pipe(
    //   map((response: any) => {
    //     return response;
    //   })
    // )
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getFeesPaymentInvoiceData/` + Id).toPromise()).data;
  }

  async getFeesReceiptData(Id): Promise<any> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getFeesReceiptData/` + Id).toPromise()).data;
  }

  async getAllFeesReceipt(Id): Promise<any> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getAllFeesReceipt/` + Id).toPromise()).data;
  }
  getAllFeesByStudent(id) {
    return this.httpClient.get(`${this.apiUrl}/getAllFeesByStudent/` + id).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  collectStudentFees(data) {
    return this.httpClient.post(`${this.apiUrl}/collectStudentFees/`, data).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  bulkCancelStudentFees(data) {
    return this.httpClient.post(`${this.apiUrl}/bulkCancelStudentFees/`, data).pipe(
      map((response: any) => {
        return response;
      })
    )
  }

  bulkDeleteFeesReceipt(data) {
    return this.httpClient.post(`${this.apiUrl}/bulkCancelFeesReceipt/`, data).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  bulkRevertCancelFeesReceipt(data) {
    return this.httpClient.post(`${this.apiUrl}/bulkRevertCancelFeesReceipt/`, data).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  checkDiscountCouponCode(CouponCode) {
    return this.httpClient.post(`${this.apiUrl}/checkDiscountCouponCode/`, {CouponCode:CouponCode}).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  async getStudentFeesById(Id: number): Promise<any[]> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getStudentFeesById/` + Id).toPromise()).data;
  }
  approveFeesPayment(studentData) {
    let params = {
      FeesPaymentIds: studentData
    }
    return this.httpClient.post(`${this.apiUrl}/approveFeesPayment`, params).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  public getAllFee(): Observable<DataResult> {
    return this.httpClient.get(`${this.apiUrl}/getAllFees/`).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        } as GridDataResult
      })
    ) as any as Observable<GridDataResult>
  }
  // Fees Payment Functions
  public getAllFeesPayment(data): Observable<DataResult> {
    return this.httpClient.post(`${this.apiUrl}/getAllFeesPayment`, data).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        } as GridDataResult
      })
    ) as any as Observable<GridDataResult>
  }
  getFeesPaymentHistoryListExcel() {
    return new Promise((resolve, reject) => {
      this.httpClient.get(`${this.apiUrl}/getFeesPaymentHistory`).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          reject(err);
        });
    });
  }
  updateFeesInvoiceDate(req) {
    return this.httpClient.post(`${this.apiUrl}/updateFeesInvoiceDate`, req).map((response: any) => {
      return response;
    })
  }
  // Discount API functions
  manageDiscount(discount) {
    return this.httpClient.post(`${this.apiUrl}/manageDiscount`, discount).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  public getAllDiscount(): Observable<DataResult> {
    return this.httpClient.get(`${this.apiUrl}/getAllDiscount/`).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        } as GridDataResult
      })
    ) as any as Observable<GridDataResult>
  }
  public getAllFeesPaymentForStudent(): Observable<DataResult> {
    return this.httpClient.get(`${this.apiUrl}/getAllFeesPaymentForStudent/`).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        } as GridDataResult
      })
    ) as any as Observable<GridDataResult>
  }



  // Razorpay 
  public feesRazorpayCreateOrder(razorPayData) {
    return this.httpClient.post(`${this.apiUrl}/feesRazorpayCreateOrder`, razorPayData).pipe(
      map((response: any) => {
        return {
          data: response.data,
          message: response.message,
          status: response.status
        }
      })
    ) as any
  }
  public feesRazorpayOrderSuccess(razorPayResponseData) {
    let body = {
      OrderId: razorPayResponseData.razorpay_order_id,
      PaymentId: razorPayResponseData.razorpay_payment_id,
      RazorpaySignature: razorPayResponseData.razorpay_signature,
      FeesPaymentId: razorPayResponseData.FeesPaymentId
    }
    return this.httpClient.post(`${this.apiUrl}/feesRazorpayOrderSuccess`, body).pipe(
      map((response: any) => {
        return response
      })
    ) as any
  }
  public feesRazorpayOrderFail(razorPayFailureData) {
    let body = {
      FeesPaymentId: razorPayFailureData.FeesPaymentId
    }
    return this.httpClient.post(`${this.apiUrl}/feesRazorpayOrderFailed`, body).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        }
      })
    ) as any
  }

  ////////// Bank CRUD //////
  manageBank(Bank) {
    return this.httpClient.post(`${this.apiUrl}/manageBank`, Bank).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  public getAllBank(): Observable<DataResult> {
    return this.httpClient.get(`${this.apiUrl}/getAllBank/`).pipe(
      map((response: any) => {
        return {
          data: response.data,
          total: response.total
        } as GridDataResult
      })
    ) as any as Observable<GridDataResult>
  }
  bulkDeleteBank(sectionIdNameList: Array<DataToDelete> = []) {
    return this.httpClient.post(`${this.apiUrl}/bulkDeleteBank/`, {
      DataToDelete: sectionIdNameList
    }).pipe(
      map((response: any) => {
        return response;
      }))
  }
  getBankById(Id: number) {
    return this.httpClient.get(`${this.apiUrl}/getBankById/` + Id).pipe(
      map((response: any) => {
        return response;
      })
    )
  }
  // public getAllBankName(): Observable<DataResult> {
  //   return this.httpClient.get(`${this.apiUrl}/getAllBankName/`).pipe(
  //     map((response: any) => {
  //       return {
  //         data: response.data,
  //         total: response.total
  //       } as GridDataResult
  //     })
  //   ) as any as Observable<GridDataResult>
  // }

  async getAllBankName(): Promise<any> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/getAllBankName/`).toPromise()).data;
  }

  async SendDuePdcDateEmailToAdmin(): Promise<any> {
    return (await this.httpClient.get<GridDataResult>(`${this.apiUrl}/SendDuePdcDateEmailToAdmin`).toPromise()).data;
  }

  bulkAddPreviousFees(previousFeesData) {
    return this.httpClient.post(`${this.apiUrl}/bulkAddPreviousFees/`, previousFeesData).pipe(
      map((response: any) => {
        return response;
      })
    )
  }

  public downloadFeesPaymentReport(data) {
    return new Promise((resolve, reject) => {
      this.httpClient.post(`${this.apiUrl}/downloadFeesPaymentReportPDF`, data, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 422) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }

  public downloadFeesPaymentReportExcel(data) {
    return new Promise((resolve, reject) => {
      this.httpClient.post(`${this.apiUrl}/downloadFeesPaymentReportExcel`, data, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 422) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }

  public downloadFeesPaymentReportStudentWiseFee(data) {
    return new Promise((resolve, reject) => {
      let newData = {
        StartDate: data.DueStartDateStudentWise,
        EndDate: data.DueEndDateStudentWise,
        LocationId: data.LocationId,
        BoardIds: data.BoardIds,
        ClassIds: data.ClassIds,
        // Status: data.StudentWiseStatus,
        DateTypeFilter:data.DateFilterType,
        DatePeriod:data.DatePeriod

      }
      
      this.httpClient.post(`${this.apiUrl}/downloadFeesReportStudentWisePDF`, newData, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 422) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }


  public downloadFeesPaymentReportExcelStudentWise(data) {
    return new Promise((resolve, reject) => {
      let newData = {
        StartDate: data.DueStartDateStudentWise,
        EndDate: data.DueEndDateStudentWise,
        LocationId: data.LocationId,
        BoardIds: data.BoardIds,
        ClassIds: data.ClassIds,
        // Status: data.StudentWiseStatus,
        DateTypeFilter:data.DateFilterType,
        DatePeriod:data.DatePeriod
      }
      this.httpClient.post(`${this.apiUrl}/downloadFeesReportStudentWiseExcel`, newData, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 422) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }

  public downloadFeesPaymentReportClassWiseFee(data) {
    return new Promise((resolve, reject) => {
      let newData = {
        StartDate: data.DueStartDateStudentWise,
        EndDate: data.DueEndDateStudentWise,
        LocationId: data.LocationId,
        BoardIds: data.BoardIds,
        ClassIds: data.ClassIds,
        // Status: data.StudentWiseStatus,
        DateTypeFilter:data.DateFilterType,
        DatePeriod:data.DatePeriod

      }
      this.httpClient.post(`${this.apiUrl}/downloadFeesReportClassWisePDF`, newData, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 422) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }


  public downloadFeesPaymentReportExcelClassWise(data) {
    return new Promise((resolve, reject) => {
      let newData = {
        StartDate: data.DueStartDateStudentWise,
        EndDate: data.DueEndDateStudentWise,
        LocationId: data.LocationId,
        BoardIds: data.BoardIds,
        ClassIds: data.ClassIds,
        // Status: data.StudentWiseStatus,
        DateTypeFilter:data.DateFilterType,
        DatePeriod:data.DatePeriod

      }
      this.httpClient.post(`${this.apiUrl}/downloadFeesReportClassWiseExcel`, newData, { responseType: 'blob' }).pipe(
        map(res => res))
        .subscribe(res => {
          resolve(res);
        }, (err) => {
          if (err.status == 422) {
            this.toasterService.showToaster(null, "No data found!", ToasterType.Error)
          }
          reject(err);
        });
    });
  }
  manageFeesConfig(data) {
    return this.httpClient.post(`${this.apiUrl}/manageFeesConfig`, data).pipe(
      map((response: any) => {
        return response;
      })
    )
  }

  getAllFeesConfig() {
    return this.httpClient.get(`${this.apiUrl}/getAllFeesConfig/`).pipe(
      map((response: any) => {
        return response;
      })
    )
  }

  getFeesConfigId(Id) {
    return this.httpClient.get(`${this.apiUrl}/getAllFeesConfig/` + Id).pipe(
      map((response: any) => {
        return response;
      })
    )
  }

  ManageBranchCronConfig(data) {
    return this.httpClient.post(`${this.apiUrl}/manageBranchCronConfig`, data).pipe(
        map((response: any) => {
            return response;
        })
    );
  }

  GetBranchCronConfig() {
    return this.httpClient.get(`${this.apiUrl}/getBranchCronConfig`).pipe(
        map((response: any) => {
            return response;
        })
    );
  }

  convertStudentFees(id) {    
    return this.httpClient.post(`${this.apiUrl}/convertStudentFees/` + id, {}).pipe(
      map((response: any) => {
        return response;
      })
    )
  }

}
